import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseUrl, getConfig, fetchLandlordIds } from '../../../../../serverUrl';
import { handleUnauthorized } from '../../auth401.ts';
import { UnpaidInvoice } from './interface';

// Helper: triggers logout and returns RTK error shape if response is 401
function checkAuth(response: Response): { error: { status: number; data: string } } | null {
    if (response.status === 401) {
        handleUnauthorized();
        return { error: { status: 401, data: 'Unauthorized' } };
    }
    return null;
}

// Fetch landlord ID asynchronously before making API requests
const getLandlordId = async () => {
    try {
        const landlordId = await fetchLandlordIds();
        return landlordId;
    } catch (error) {
        console.error('Error fetching Landlord ID:', error);
        return null;
    }
};

// Get authorization config
const getConfigData = () => getConfig();

export const invoicingSlice = createApi({
    reducerPath: 'InvoicingApi',
    baseQuery: fetchBaseQuery({ baseUrl }),
    tagTypes: ['Invoicing'],
    endpoints: (builder) => ({
        getUnpaidInvoices: builder.query<UnpaidInvoice[], void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    const response = await fetch(`${baseUrl}/landlord/${landlordId}/unpaid-invoices`, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Invoicing'],
        }),

        getTYPECInvoices: builder.query<UnpaidInvoice[], void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    const response = await fetch(`${baseUrl}/tenant/get_type_c_invoice/?landlord_id=${landlordId}`, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Invoicing'],
        }),

        getMissingEtimsInvoices: builder.query<any, { month?: string; year?: string }>({
            queryFn: async (args = {}) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    const params = new URLSearchParams();
                    params.append('landlord_id', String(landlordId));
                    if (args.month) params.append('month', args.month);
                    if (args.year) params.append('year', String(args.year));
                    const response = await fetch(`${baseUrl}/tenant/get_missing_etims_invoices/?${params.toString()}`, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch missing eTIMS invoices" } };
                }
            },
            providesTags: ['Invoicing'],
        }),

        addTypeCInvoicing: builder.mutation<void, any>({
            queryFn: async (bodyData) => {
                try {
                    const response = await fetch(`${baseUrl}/tenant/post_kra_type_c_invoices/`, {
                        method: 'POST',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(bodyData),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    if (!response.ok) {
                        return { error: { status: response.status, data } };
                    }
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch apartment data" } };
                }
            },
            invalidatesTags: ['Invoicing'],
        }),

        getUngeneratedInvoices: builder.query({
            queryFn: async (args: { month: string; year: string }, { getState }) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    // Construct query parameters
                    const params = new URLSearchParams({
                        landlord_id: landlordId,
                        month: args.month,
                        year: args.year
                    });

                    const response = await fetch(
                        `${baseUrl}/landlord/invoice-generating-summary/?${params.toString()}`,
                        getConfigData()
                    );
                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to fetch invoice data"
                        }
                    };
                }
            },
            providesTags: ['Invoicing'],
        }),
        getLandlordInvoices: builder.query({
            queryFn: async (args: { month?: string; year?: string; reference?: string; apartment_id?: string; house_id?: string; status?: string }) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams({
                        landlord_id: landlordId,
                    });
                    if (args?.month) params.append('month', args.month);
                    if (args?.year) params.append('year', args.year);
                    if (args?.reference) params.append('reference', args.reference);
                    if (args?.apartment_id) params.append('apartment_id', args.apartment_id);
                    if (args?.house_id) params.append('house_id', args.house_id);
                    if (args?.status) params.append('status', args.status);

                    const response = await fetch(
                        `${baseUrl}/landlord/landlord-invoices/?${params.toString()}`,
                        getConfigData()
                    );
                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to fetch invoices"
                        }
                    };
                }
            },
            providesTags: ['Invoicing'],
        }),
        generateInvoices: builder.mutation<void, any>({
            queryFn: async (invoiceData) => {  // Changed parameter name to be more descriptive
                try {
                    const response = await fetch(`${baseUrl}/landlord/tenant-invoice/`, {
                        method: 'POST',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(invoiceData),  // Directly pass the invoice data
                    });
                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error.message || "Failed to generate invoice"
                        }
                    };
                }
            },
            invalidatesTags: ['Invoicing'],
        }),
        sendInvoiceSms: builder.mutation<void, { invoice_ids: number[] }>({
            queryFn: async (bodyData) => {
                try {
                    const response = await fetch(`${baseUrl}/landlord/tenant-invoice/send-sms/`, {
                        method: 'POST',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(bodyData),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    if (!response.ok) {
                        return {
                            error: {
                                status: response.status,
                                data: data?.error || data || "Failed to send invoice SMS"
                            }
                        };
                    }
                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to send invoice SMS"
                        }
                    };
                }
            },
        }),
        deleteInvoice: builder.mutation<any, number>({
            queryFn: async (invoiceId: number) => {
                try {
                    const response = await fetch(`${baseUrl}/landlord/tenant-invoice/${invoiceId}/`, {
                        method: 'DELETE',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                    });
                    const authError = checkAuth(response); if (authError) return authError;

                    let data: any = null;
                    const contentType = response.headers.get('content-type') || '';
                    if (contentType.includes('application/json')) {
                        data = await response.json();
                    } else {
                        const text = await response.text();
                        data = text || null;
                    }

                    if (!response.ok) {
                        return {
                            error: {
                                status: response.status,
                                data: data?.error || data?.detail || data || `HTTP error! status: ${response.status}`
                            }
                        };
                    }

                    return { data: data || { success: true } };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to delete invoice"
                        }
                    };
                }
            },
            invalidatesTags: ['Invoicing'],
        }),
        deleteKraInvoice: builder.mutation<void, number>({
            queryFn: async (invoiceId: number) => {
                try {
                    const response = await fetch(`${baseUrl}/landlord/tenant-invoice/${invoiceId}/delete-kra-invoice/`, {
                        method: 'DELETE',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    if (!response.ok) {
                        return {
                            error: {
                                status: response.status,
                                data: data?.error || data || "Failed to delete KRA invoice"
                            }
                        };
                    }
                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to delete KRA invoice"
                        }
                    };
                }
            },
            invalidatesTags: ['Invoicing'],
        }),
        addCreditNote: builder.mutation<void, any>({
            queryFn: async (bodyData) => {
                try {
                    const response = await fetch(`${baseUrl}/tenant/post_kra_credit_notes/`, {
                        method: 'POST',
                        headers: {
                            ...getConfigData().headers,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(bodyData),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to post credit note" } };
                }
            },
            invalidatesTags: ['Invoicing'],
        }),

    }),
});

// Export hooks for usage in components
export const {
    useGetUnpaidInvoicesQuery,
    useGetTYPECInvoicesQuery,
    useGetMissingEtimsInvoicesQuery,
    useLazyGetMissingEtimsInvoicesQuery,
    useAddTypeCInvoicingMutation,
    useAddCreditNoteMutation,
    useGetUngeneratedInvoicesQuery,
    useGetLandlordInvoicesQuery,
    useGenerateInvoicesMutation,
    useSendInvoiceSmsMutation,
    useDeleteInvoiceMutation,
    useDeleteKraInvoiceMutation
} = invoicingSlice;

